// A summary document template is a list of sections. Each section is one field of the result schema plus how the
// document shows it. This module converts between that list and the stored `result_schema` + `display`, so people
// edit sections and never JSON. Anything a section doesn't model (patterns, enums, extra display keys) is kept.

export const STYLES = [
  { value: 'sourced_text', label: 'Paragraph', shape: 'text', hint: 'A paragraph of prose.' },
  { value: 'sourced_list', label: 'Bulleted list', shape: 'list', hint: 'Bullets. The first field is the bullet; the others show underneath.' },
  { value: 'key_value', label: 'Labelled fields', shape: 'object', hint: 'A box of label: value pairs.' },
  { value: 'table', label: 'Table', shape: 'list', hint: 'One row per item, one column per field.' },
  { value: 'issue_action_list', label: 'Issues and actions', shape: 'list', fixed: true, hint: 'Each item is an issue with a recommended action.' },
  { value: 'assessment_list', label: 'Assessment list', shape: 'list', hint: 'A list of assessments with details.' },
  { value: 'text_block', label: 'Plain text', shape: 'text', hint: 'Unformatted text.' },
  { value: 'accuracy_badge', label: 'Accuracy badge', shape: 'object', fixed: true, hint: 'A level with an explanation.' },
];

const STYLE_BY_VALUE = Object.fromEntries(STYLES.map((s) => [s.value, s]));

const FIXED_FIELDS = {
  issue_action_list: [
    { name: 'issue', description: 'The issue identified.' },
    { name: 'recommendation', description: 'The recommended action.' },
  ],
  accuracy_badge: [
    { name: 'level', description: 'High, medium or low.' },
    { name: 'explanation', description: 'Why this level.' },
  ],
};

export function shapeOf(style) {
  return (STYLE_BY_VALUE[style] || STYLE_BY_VALUE.sourced_text).shape;
}

export function usesFields(style) {
  return ['list', 'object'].includes(shapeOf(style));
}

export function isFixed(style) {
  return !!(STYLE_BY_VALUE[style] || {}).fixed;
}

function clone(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

function objectFields(schema) {
  const props = (schema && schema.properties) || {};
  return Object.entries(props).map(([name, spec]) => ({ name, description: (spec && spec.description) || '', spec: clone(spec) }));
}

function fieldsOf(spec) {
  if (!spec) return [];
  if (spec.type === 'object') return objectFields(spec);
  if (spec.type === 'array' && spec.items && spec.items.type === 'object') return objectFields(spec.items);
  return [];
}

function slug(text) {
  return String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
}

export function uniqueKey(label, taken) {
  const base = slug(label) || 'section';
  let key = base;
  let n = 2;
  while (taken.includes(key)) { key = `${base}_${n}`; n += 1; }
  return key;
}

/**
 * Read sections from a template's result schema and display. Shown sections come first in display order, then
 * fields the AI produces but the document doesn't show.
 */
export function sectionsFrom(resultSchema, display) {
  const props = (resultSchema && resultSchema.properties) || {};
  const shown = (display && display.display_sections) || [];
  const pdf = display && Array.isArray(display.pdf_sections) ? new Set(display.pdf_sections.map((s) => s.key)) : null;
  const seen = new Set();
  const sections = [];
  shown.forEach((entry) => {
    if (!entry || !(entry.key in props) || seen.has(entry.key)) return;
    seen.add(entry.key);
    const { key, label, style, ...extra } = entry;
    const spec = props[key];
    const fields = fieldsOf(spec);
    if (style === 'table' && Array.isArray(extra.columns)) fields.forEach((f) => { f.column = extra.columns.includes(f.name); });
    sections.push({
      key, label: label || '', hadLabel: label !== undefined, style: style || 'sourced_text', description: (spec && spec.description) || '',
      fields, shown: true, inPdf: pdf ? pdf.has(key) : true, spec: clone(spec), extra: clone(extra),
    });
  });
  Object.entries(props).forEach(([key, spec]) => {
    if (seen.has(key)) return;
    sections.push({
      key, label: '', style: spec && spec.type === 'array' ? 'sourced_list' : (spec && spec.type === 'object' ? 'key_value' : 'sourced_text'),
      description: (spec && spec.description) || '', fields: fieldsOf(spec), shown: false, inPdf: false, spec: clone(spec), extra: {},
    });
  });
  return sections;
}

function stringField(description) {
  return description ? { type: 'string', description } : { type: 'string' };
}

function objectSchema(fields, previous) {
  const properties = {};
  fields.forEach((f) => {
    if (!f.name) return;
    const kept = f.spec && typeof f.spec === 'object' ? clone(f.spec) : stringField('');
    if (f.description) kept.description = f.description; else delete kept.description;
    properties[f.name] = kept;
  });
  const names = Object.keys(properties);
  const kept = ((previous && previous.required) || []).filter((k) => names.includes(k));
  const required = [...kept, ...names.filter((k) => !kept.includes(k))];
  return { ...(previous || {}), type: 'object', required, properties, additionalProperties: false };
}

/** The result-schema property for one section. Keeps the parts of the old spec the builder doesn't model. */
export function specFor(section) {
  const shape = shapeOf(section.style);
  const old = section.spec || {};
  const fields = isFixed(section.style) && !section.fields.length ? FIXED_FIELDS[section.style] : section.fields;
  let spec;
  if (shape === 'text') {
    // A "sourced" paragraph is an object: text plus confidence, quote or source. Keep those fields.
    const sourced = old.type === 'object' && fields.some((f) => f.name === 'text');
    spec = sourced ? objectSchema(fields, old) : (old.type === 'string' ? { ...old } : { type: 'string' });
  } else if (shape === 'object') {
    spec = objectSchema(fields, old.type === 'object' ? old : null);
  } else if (fields.length) {
    const oldItems = old.type === 'array' && old.items && old.items.type === 'object' ? old.items : null;
    spec = { ...(old.type === 'array' ? old : {}), type: 'array', items: objectSchema(fields, oldItems) };
  } else {
    spec = old.type === 'array' && old.items && old.items.type === 'string' ? { ...old } : { type: 'array', items: { type: 'string' } };
  }
  if (section.description) spec.description = section.description; else delete spec.description;
  return spec;
}

/**
 * Build `result_schema` and `display` from sections. `baseSchema` and `baseDisplay` carry everything else
 * (top-level schema keys, display.work_type and other display settings) through unchanged.
 */
export function toTemplate(sections, baseSchema, baseDisplay) {
  // Property order is the order the model writes fields in, so it stays as it was; reordering sections only
  // reorders the document. New sections are appended.
  const bySection = Object.fromEntries(sections.map((s) => [s.key, s]));
  const before = Object.keys((baseSchema && baseSchema.properties) || {}).filter((k) => k in bySection);
  const order = [...before, ...sections.map((s) => s.key).filter((k) => !before.includes(k))];
  const properties = Object.fromEntries(order.map((k) => [k, specFor(bySection[k])]));
  const oldRequired = ((baseSchema && baseSchema.required) || []).filter((k) => k in properties);
  const required = [...oldRequired, ...order.filter((k) => !oldRequired.includes(k))];
  const resultSchema = { ...(baseSchema || {}), type: 'object', required, properties, additionalProperties: false };
  const entry = (s) => {
    const out = { key: s.key, ...(s.label || s.hadLabel ? { label: s.label } : {}), style: s.style, ...(s.extra || {}) };
    if (s.style === 'table') {
      // Keep the table's own column order; new fields become new columns at the end.
      const names = s.fields.filter((f) => f.column !== false).map((f) => f.name).filter(Boolean);
      const kept = ((s.extra || {}).columns || []).filter((c) => names.includes(c));
      out.columns = [...kept, ...names.filter((n) => !kept.includes(n))];
    }
    else delete out.columns;
    return out;
  };
  const shown = sections.filter((s) => s.shown);
  const display = { ...(baseDisplay || {}), display_sections: shown.map(entry) };
  if (shown.every((s) => s.inPdf)) delete display.pdf_sections;
  else display.pdf_sections = shown.filter((s) => s.inPdf).map(entry);
  return { resultSchema, display };
}

export function newSection(label, style, taken) {
  const fields = clone(FIXED_FIELDS[style]) || (usesFields(style) ? [{ name: 'text', description: '' }] : []);
  return { key: uniqueKey(label, taken), label, style, description: '', fields, shown: true, inPdf: true, spec: null, extra: {} };
}

/** Changing a section's style keeps its fields where the new style uses them. */
export function restyle(section, style) {
  const next = { ...section, style, spec: null };
  if (FIXED_FIELDS[style]) next.fields = clone(FIXED_FIELDS[style]);
  else if (usesFields(style) && !section.fields.length) next.fields = [{ name: 'text', description: '' }];
  else if (!usesFields(style)) next.fields = [];
  if (style !== 'table') next.extra = { ...(section.extra || {}) };
  if (next.extra) delete next.extra.columns;
  return next;
}

/** Placeholder content so the preview shows every section before a draft has been run. */
export function sampleContent(sections) {
  const content = {};
  sections.forEach((s) => {
    const shape = shapeOf(s.style);
    const row = () => Object.fromEntries(s.fields.map((f) => [f.name, `${f.name.replace(/_/g, ' ')}…`]));
    if (shape === 'text') content[s.key] = `${s.label || s.key}: the AI writes this paragraph.`;
    else if (shape === 'object') content[s.key] = row();
    else if (s.fields.length) content[s.key] = [row(), row()];
    else content[s.key] = ['First item…', 'Second item…'];
  });
  return content;
}
